import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, attendanceAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkInRequired, setCheckInRequired] = useState(false);
  const [checkInStatusLoading, setCheckInStatusLoading] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await authAPI.getCurrentUser();
        const authenticatedUser = response.data.user;
        setUser(authenticatedUser);
        await refreshCheckInStatus(authenticatedUser);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const refreshCheckInStatus = async (currentUser = user) => {
    if (!currentUser || currentUser.role !== 'employee') {
      setCheckInRequired(false);
      return;
    }
    setCheckInStatusLoading(true);
    try {
      const res = await attendanceAPI.getMyTodayAttendance();
      const todayAttendance = res.data;
      setCheckInRequired(!todayAttendance?.check_in);
    } catch (err) {
      setCheckInRequired(true);
    } finally {
      setCheckInStatusLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await authAPI.login(credentials);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      await refreshCheckInStatus(user);
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
    setCheckInRequired(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  // Store-based access control
  const canAccessStore = (storeId) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.store_id === storeId;
  };

  const canModifyData = () => {
    if (!user) return false;
    return ['admin', 'manager', 'employee'].includes(user.role);
  };

  const canCreateData = () => {
    if (!user) return false;
    return ['admin', 'manager', 'employee'].includes(user.role);
  };

  const canDeleteData = () => {
    if (!user) return false;
    return ['admin', 'manager', 'employee'].includes(user.role);
  };

  const getUserStore = () => {
    return {
      id: user?.store_id,
      name: user?.store_name,
      city: user?.store_city
    };
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isManager = () => {
    return user?.role === 'manager';
  };

  const isEmployee = () => {
    return user?.role === 'employee';
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateUser,
    isAuthenticated,
    hasRole,
    hasAnyRole,
    canAccessStore,
    canModifyData,
    canCreateData,
    canDeleteData,
    getUserStore,
    isAdmin,
    isManager,
    isEmployee,
    checkInRequired,
    checkInStatusLoading,
    refreshCheckInStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 